import { ConnectionState, PresenceMessage, RealtimeChannel } from 'ably'
import { assign, PromiseActorLogic, setup } from 'xstate'

export const oneUserMachine = setup({
  types: {
    context: {} as {
      isViewOnly: boolean
      members: PresenceMessage[]
      realtimeChannel?: RealtimeChannel
      connectionState?: ConnectionState
      myConnectionId?: string
    },
    input: {} as {
      isViewOnly: boolean
    },
    events: {} as
      | {
          type: 'ON_CONNECT'
          members: PresenceMessage[]
          myConnectionId?: string
        }
      | { type: 'HANDLE_USER_ENTER'; members: PresenceMessage[] }
      | { type: 'HANDLE_USER_LEAVE'; members: PresenceMessage[] }
      | { type: 'HANDLE_ENTER'; members: PresenceMessage[] }
      | { type: 'END_USER_SESSION' }
      | { type: 'CONNECTION_STATE_CHANGE'; connectionState: ConnectionState }
      | { type: 'CONNECTION_LOST' }
      | { type: 'CLOSE' }
      | { type: 'CANCEL' },
  },
  guards: {
    isFlowOccupied: ({ context }) => context.members?.length > 0,
    isOnlyMe: ({ context }) =>
      context.members?.length === 1 &&
      context.members[0].connectionId === context.myConnectionId,
    isOffline: ({ context }) => context.connectionState === 'disconnected',
    isViewOnly: ({ context }) => context.isViewOnly,
  },
  actors: {} as {
    initializeAbly: PromiseActorLogic<RealtimeChannel>
    loadFlow: PromiseActorLogic<void>
    enterChannel: PromiseActorLogic<void>
    openLobbyModal: PromiseActorLogic<void>
    endMySession: PromiseActorLogic<void>
    saveFlowBeforeExit: PromiseActorLogic<void>
    expireSession: PromiseActorLogic<void>
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QHkB2YCqswCcCyAhgMYAWAlugMQDCyAcnQKLUAqAkvQPoDKLAgi0adqACT50A4owDaABgC6iUAAcA9rDIAXMqtRKQAD0QBaAJwAmABwA6ACwB2AKwBGe7PMA2W4-vPbAGhAATxNnWQ9TawBmR0dLewtbb3N7WwBfNMC0TGx8YnJ0a1IwIgBrCigANTIwAHc0ABsgvFUIMEo5RSQQNQ1tXX0jBGNbWWdrD3tLKOcPKMtLbwTAkOHFx2tTWXso+0nnc1ko2w8MrPQsXEJSCjAikhLy1Cqa+tQmlraO5y6VdS0dHpukNjFFTJF3IcPC5ZlEZnsViZ7OZIltHLZnM5TAlkeZzGcQNlLnkboUKACCA0yAAvCp8ABGTUoEF0dwoADdVKU7kTctcCmzUBSqbTngymggOaoiAR+qhOp19L0AQNgYhnFFop5LK4Es5Yi4AsEkVjrO4vNjbJ4PBFLATeVd8rdrGQIA12lxaAxmCxFd1lXLBogouYNuY4bJLD5Q4tFojhsjZNZHJ4fKYNVDHPaLnynYVimU6UQiHBYB0FEr-oG1Qg3PZrL5LKYUxZwmD7PHjBEw+DTBErQtZCnszlHaS7gWnlA+MXS99fj0q4Cg7XzLZoltFrNDoskp2XOYzbJbHDpge9ulMoSc2OBdZiCXYBpnhhUNKAK7KZQ1CDUEgEVB0AaSgxDoAARAAZIRGDoQQACU-T+PplxrVwxiPS0sRSXZHH3I5rBSBxoWPFFDnxK8HRJO8H1LCpkCIT9v0gP8AKAkDxEg6DYMYBCK39JdVVAIY0PGWRZEwiwdlSSxO0TZNUzXBJsUWEdiX5Z1agIAFnnY8CoM4DBuB4zgoL4SoZD4pCVSBIT1WxUSU2tI5sXCTtFiTLYMT2A15nsVTc3HaxNO0qBdM4gyjLgzgYPgxDF2QwTDEQcxnHiTZTFGBxZhTDwZONBNDnk6FFPBKZL3OUcqI0rTtB0uKAxQ2yEFSqNrEsLwT0WOEPDEqJOw8MJNkHOtQwylJ-NvaqQsoAxYE0WU7gIAAzTRcAACiiMSxIASkoSj1MKYLaqgeqBJspKEEOVxkwSDw10xNdev6zEG3mSNRh2KMhwmqrCgaVQCAgCpqjqRoggAMX+2pmVZF1UE5blrH2vM7n+wHgdeMHIdUWpJXh6VZUBBVLPi6yVzXaw-F8FKsXRTbsLck4Jh8UZdh3MZyuvSqDtRgGgeebHoZZMl8cR5HArR-moEFvHORlOViYXBrEqGPFTTE1xbHa5F5hOTsHBsBIdWxSTcT8iib1+3n0YFqGwv0wzjJinjToS87VdS+twTBIi3A1-rtgIlL7CcTEtYy0wfp56xIC0QX7aER2oud3ilbO8mzwbMYQ9DXYwmOPDNUI1JXBDbwswt7mUZjoHNHjr0mFYDg6BM5BeFdsmazxUwbBPPYZmmWYbU7XWGw1SxI1ifU4gyqPq9gAh2QqePhcFBGeUt6OF6X22cdlgmFYUDvqya7rNSxPsox1SwUtc-KRimBse6xBIUjNueJdUel6SCSgYLAiKxkjLcG4M3Y+jULqYlMJqDKLgUwxAiPqPq98Ij1liFtCeE8ZixA-nef639f6gXCknEyjAzIWTTm7FcYRwSU3mD4IcMQdQhjcl4M02C7rhkbFYXBzp8E-wToA5O3FU6VioTWCe4xUrbFynEWEthTCsPXEcfUnC843ztJXNS1d+G-2oOIagjAILgJVogZ+0QJ6jBTJGLwqUA71niHMfUhwfA2lOFogKd5sBPkBIwVAbQIA0Agm3ChYjO6nzcRMfYSDwgOH1hPAiPdDifUcGzZwvDCjeI0LoRgBhvw4EgEEkJJj3bJSmDYdE7VMRuHTNMTsGokwsy2gNT2riMl3E0DgIIFQWCqDgiUXQ6AiCaD2i3BuPoSnk0cOJNqSRwyTBDOiDs98QyajmFsZsId0TWPcRVbRgVOndOeL0-pRBBklBGUQh2kVSHkMmV3GmZpwwLDhLsKY9TFh2DXCidMWIDg6k5uLO8hyel9IGYBC5M05oLXvCtdam0tq7SBc6EFxywVnIhcM+5TVvmNN8KlEMTZs5GlWMYVwNg5ipC2DQ5xWt2n3EeEWR8sA+BwpwKc85wzyyUPCRdTwFgCIT08CkdqjDkGkvmIeE4krMEZS1ubPZnjnSTiZaWVlq12XgqGSM6QPwwknz5drCY+oMrxEni8keN87BzA0ZGOV8QMhXjfG0eA3RkXoH1RAkENTBWjHBDaMEk9OzNkiPqKSa4QypGPPSlVzwQZvA+K0MAnrTHDBtOMKwfq+zrKDffWMlMfA+wnlMFq9LyTaEpDSOkjJVhWQNSCFKh44jhDDXCJwF5ZIpTNNMo4x4NSzCSGWt0yb+LiJxddUudMvCOC8PEcw-VsTJgUT3EMsSe67K5vsu8sbpyzifCm0pCAXAeAItgpwQ4UoLAXWg5d0woQKPavSmiPiXxvgYl+H8LEIUNAPdQq0GxoE3z8CmFwCwSUmGmZqI2Q8XHbFSE+vdz4oD0UYp+-837f2oRPOuJwUYrE+BanlUlyJRLLrGk4OEsx6VHQqJhpqWFNQhk8G4G+PcrHXuTD2yM3HsEV0VZNP6fMMag3eBDKGdGLrGBSsosIKQ5jHgWLENyvhmafRDj3aB2J6VLzqILCTHt1z6mcU2UMbj515oUXYbYMQ+zHBOH2elktl7idHby4SPd6xeGDseUYx4wSFyfjqG0sGQ6As3tXWOdcXN1q9clQ0nH0EHFiCcPW997PRFsTO1wsQTP0u3s5nG+ngziUPE2G0myQ6bWcAF6DwWhxwbC1XT+BCisIAsIeMYq7pPhGpp2hyYlG3hHElEDd7q7hZN8f4yArWRszN8AkKxLjoH2IbN4WY-qmy1Ly7RHJeSyAFIgK10MKndjYnbXsKw7V6nojNFG3Y8RYgjb45upVhRUVQBOVqi5R3NoAYxN4aBdY3EjzElE0MWJ3rtfSR4gTE4HiFjFIh9VuAOWYs0Ed20BEFMWG6tfEHSZgsuE3AotWMb-rYEO65+twYvAwInqkvUUaNT63GAo2lOxZG9UdWkIAA */
  id: 'OneUserMachine',
  context: ({ input }) => ({
    members: [],
    isViewOnly: input.isViewOnly,
  }),
  initial: 'checkingViewOnlyMode',
  states: {
    checkingViewOnlyMode: {
      always: [
        {
          guard: 'isViewOnly',
          target: 'loadingViewOnlyFlow',
        },
        {
          target: 'initializingAbly',
        },
      ],
    },

    initializingAbly: {
      invoke: {
        src: 'initializeAbly',
        onDone: {
          target: 'idle',
          actions: assign({ realtimeChannel: ({ event }) => event.output }),
        },
      },
    },

    idle: {
      on: {
        ON_CONNECT: {
          actions: assign({
            members: ({ event }) => event.members,
            myConnectionId: ({ event }) => event.myConnectionId,
          }),
          target: 'checkingAccess',
        },
      },
    },

    checkingAccess: {
      always: [
        {
          guard: 'isFlowOccupied',
          target: 'lobby',
        },
        {
          target: 'accessingUnocuppiedChannel',
        },
      ],
    },

    accessingUnocuppiedChannel: {
      invoke: {
        src: 'enterChannel',
      },
      on: {
        HANDLE_ENTER: {
          target: 'loadingFlow',
          actions: assign({ members: ({ event }) => event.members }),
        },
      },
    },

    accessingOcuppiedChannel: {
      invoke: {
        src: 'enterChannel',
      },
      on: {
        HANDLE_ENTER: {
          target: 'waiting',
          actions: assign({ members: ({ event }) => event.members }),
        },
      },
    },

    waiting: {
      always: [
        {
          guard: 'isOnlyMe',
          target: 'loadingFlow',
        },
      ],
      after: {
        1: {
          target: 'loadingFlow',
        },
      },
      on: {
        HANDLE_USER_LEAVE: {
          target: 'waiting',
          actions: assign({ members: ({ event }) => event.members }),
          reenter: true,
        },
        HANDLE_USER_ENTER: {
          target: 'sessionEnded',
          actions: assign({ members: ({ event }) => event.members }),
        },
      },
    },

    loadingViewOnlyFlow: {
      invoke: {
        src: 'loadFlow',
        onDone: 'viewFlow',
      },
      tags: 'visible',
    },

    viewFlow: {
      type: 'final',
      tags: 'visible',
    },

    loadingFlow: {
      invoke: {
        src: 'loadFlow',
        onDone: 'editFlow',
      },
      on: {
        HANDLE_USER_ENTER: {
          target: 'sessionEnded',
          actions: assign({ members: ({ event }) => event.members }),
        },
      },
      tags: 'visible',
    },

    editFlow: {
      on: {
        HANDLE_USER_ENTER: {
          target: 'savingFlow',
          actions: assign({ members: ({ event }) => event.members }),
        },
        CONNECTION_LOST: {
          target: 'tryingToReconnect',
        },
      },
      tags: 'visible',
    },

    savingFlow: {
      invoke: {
        src: 'saveFlowBeforeExit',
        onDone: 'sessionEnded',
      },
    },

    lobby: {
      invoke: {
        src: 'openLobbyModal',
      },
      on: {
        END_USER_SESSION: {
          target: 'accessingOcuppiedChannel',
        },

        HANDLE_USER_LEAVE: {
          target: 'lobby',
          actions: assign({ members: ({ event }) => event.members }),
          reenter: true,
        },

        HANDLE_USER_ENTER: {
          target: 'lobby',
          actions: assign({ members: ({ event }) => event.members }),
          reenter: true,
        },

        CANCEL: {
          target: 'closed',
        },
      },
    },

    sessionEnded: {
      invoke: {
        src: 'endMySession',
      },

      on: {
        CLOSE: {
          target: 'closed',
        },
      },
    },

    sessionExpired: {
      invoke: {
        src: 'expireSession',
      },

      on: {
        CLOSE: {
          target: 'closed',
        },
      },
    },

    tryingToReconnect: {
      on: {
        ON_CONNECT: {
          actions: assign({ members: ({ event }) => event.members }),
          target: 'checkingAccessAfterReconnect',
        },
        HANDLE_USER_LEAVE: {
          target: 'sessionExpired',
          actions: assign({ members: ({ event }) => event.members }),
        },
      },
      after: {
        30000: [
          {
            target: 'sessionExpired',
          },
        ],
      },
      tags: 'visible',
    },

    checkingAccessAfterReconnect: {
      always: [
        {
          guard: 'isOnlyMe',
          target: 'editFlow',
        },
        {
          target: 'sessionEnded',
        },
      ],
    },

    closed: {
      type: 'final',
    },
  },
  on: {
    CONNECTION_STATE_CHANGE: {
      actions: assign({
        connectionState: ({ event }) => event.connectionState,
      }),
    },
  },
})
